/*
 * This component receives a child element that will be rendered using a
 * transition when it becomes available
 */
import * as React from "react";

import {InitialFallback} from "@khanacademy/wonder-blocks-core";

import withPrefersReducedMotion, {
    type WithoutPrefersReducedMotion,
    type WithPrefersReducedMotionProps,
} from "#data-access/with-prefers-reduced-motion.tsx";
import AphroditeCSSTransitionGroup from "#shared-styles-package/aphrodite-css-transition-group.tsx";
import {createTransition} from "#shared-styles-package/util.ts";

type OwnProps = {
    // Element that will use the fade in transition
    children: React.ReactElement<any>;
};

type Props = OwnProps & WithPrefersReducedMotionProps;

class TransitionAppear extends React.Component<Props> {
    render(): React.ReactNode {
        const {children, prefersReducedMotion} = this.props;

        /**
         * NOTE(WEB-1837): We have to wrap this in InitialFallback because
         * prefersReducedMotion accesses window.matchMedia and that means the
         * client-side result could differ from the server-side result, which
         * would break rehydration. We don't want that.
         */
        return (
            <InitialFallback fallback={() => children}>
                {() =>
                    prefersReducedMotion ? (
                        children
                    ) : (
                        <AphroditeCSSTransitionGroup
                            transitionStyle={fadeInTransition}
                            transitionAppearTimeout={duration}
                        >
                            {children}
                        </AphroditeCSSTransitionGroup>
                    )
                }
            </InitialFallback>
        );
    }
}

const duration = 300; // ms

const fadeInTransition = createTransition({
    appear: {
        opacity: 0,
    },

    appearActive: {
        opacity: 1,
        transitionDuration: `${duration}ms`,
        transitionTimingFunction: "ease-in",
    },
});

type ExportProps = WithoutPrefersReducedMotion<
    JSX.LibraryManagedAttributes<
        typeof TransitionAppear,
        React.ComponentProps<typeof TransitionAppear>
    >
>;

export default withPrefersReducedMotion(
    TransitionAppear,
) as React.ComponentType<ExportProps>;
